import { INVITE_MAX_SCHEDULE_DAYS } from "@graftus/schema";

/**
 * Send-time slots for a scheduled invitation: the next weekdays at 08:00 and
 * 13:00 in the business's time zone, at least an hour ahead and within the
 * 14-day scheduling window. Pure `Intl`, no date library.
 */
export const DEFAULT_TIME_ZONE = "Europe/Stockholm";
export const SLOT_HOURS = [8, 13] as const;
const MIN_LEAD_MS = 60 * 60 * 1000;

export interface InviteSlot {
  /** UTC instant, what the API receives. */
  iso: string;
  /** "mån 26 okt · 08:00" in the UI language. */
  label: string;
}

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
}

const partsFormatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = partsFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      weekday: "short",
    });
    partsFormatters.set(timeZone, f);
  }
  return f;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** The wall clock of an instant in a time zone. */
export function wallClock(date: Date, timeZone: string): WallClock {
  const parts = Object.fromEntries(
    partsFormatter(timeZone)
      .formatToParts(date)
      .map((p) => [p.type, p.value]),
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    weekday: WEEKDAYS.indexOf(parts.weekday ?? "Sun"),
  };
}

/** The instant at which a time zone's wall clock reads the given date and time. */
export function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  // Guess the instant as if the wall clock were UTC, then correct by the
  // zone's offset at that instant; a second pass settles DST edges.
  let guess = Date.UTC(year, month - 1, day, hour, minute);
  for (let i = 0; i < 2; i++) {
    const wc = wallClock(new Date(guess), timeZone);
    const asUtc = Date.UTC(wc.year, wc.month - 1, wc.day, wc.hour, wc.minute);
    guess += Date.UTC(year, month - 1, day, hour, minute) - asUtc;
  }
  return new Date(guess);
}

export function formatSlotLabel(date: Date, locale: string, timeZone: string): string {
  const day = new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(date);
  const time = new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
  return `${day} · ${time}`;
}

export function buildInviteSlots(
  now: Date,
  options: { count?: number; locale?: string; timeZone?: string } = {},
): InviteSlot[] {
  const count = options.count ?? 4;
  const locale = options.locale ?? "sv-SE";
  const timeZone = options.timeZone ?? DEFAULT_TIME_ZONE;
  const earliest = now.getTime() + MIN_LEAD_MS;
  const latest = now.getTime() + INVITE_MAX_SCHEDULE_DAYS * 24 * 60 * 60 * 1000;
  const slots: InviteSlot[] = [];
  const start = wallClock(now, timeZone);
  for (let offset = 0; offset < INVITE_MAX_SCHEDULE_DAYS + 1 && slots.length < count; offset++) {
    // Walk calendar days in the zone by adding days to the zone's midnight.
    const dayStart = zonedTimeToUtc(start.year, start.month, start.day + offset, 0, 0, timeZone);
    const wc = wallClock(dayStart, timeZone);
    if (wc.weekday === 0 || wc.weekday === 6) continue;
    for (const hour of SLOT_HOURS) {
      const candidate = zonedTimeToUtc(wc.year, wc.month, wc.day, hour, 0, timeZone);
      const ms = candidate.getTime();
      if (ms < earliest || ms > latest) continue;
      slots.push({ iso: candidate.toISOString(), label: formatSlotLabel(candidate, locale, timeZone) });
      if (slots.length >= count) break;
    }
  }
  return slots;
}
